import apiClient from './apiClient';

export interface Job {
  id: string;
  title: string;
  client_name: string;
  client_phone: string;
  artisan_name: string;
  artisan_phone?: string;
  budget_min: number;
  budget_max: number;
  status: 'pending' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'disputed';
  service_category?: string;
  location?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface JobListResponse {
  count: number;
  jobs: Job[];
}

class JobService {
  /**
   * Get list of jobs with optional filtering
   * @param status - Filter by job status
   * @param search - Search by job title
   * @param limit - Number of jobs to return (default: 50)
   */
  async getJobs(
    status?: string,
    search?: string,
    limit: number = 50
  ): Promise<JobListResponse> {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      params.append('limit', limit.toString());

      const response = await apiClient.get(`/admin/jobs/`, {
        params: Object.fromEntries(params),
      });

      return response.data.data;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    }
  }

  /**
   * Get detailed information about a specific job
   * @param jobId - UUID of the job
   */
  async getJobDetail(jobId: string): Promise<Job> {
    try {
      const response = await apiClient.get(`/admin/jobs/${jobId}/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching job detail:', error);
      throw error;
    }
  }

  /**
   * Reassign a job to a different artisan
   * @param jobId - UUID of the job
   * @param artisanId - UUID of the new artisan's ArtisanProfile
   */
  async reassignArtisan(jobId: string, artisanId: string): Promise<any> {
    try {
      const response = await apiClient.post(`/admin/jobs/${jobId}/reassign/`, {
        artisan_id: artisanId,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error reassigning artisan:', error);
      throw error;
    }
  }

  /**
   * Get list of artisans, optionally filtered by service category
   * @param categoryId - UUID of the service category (optional)
   * @param search - search by name/email (optional)
   */
  async getArtisans(categoryId?: string, search?: string): Promise<{ count: number; artisans: any[] }> {
    try {
      const params: Record<string, string> = {};
      if (categoryId) params.category = categoryId;
      if (search) params.search = search;
      const response = await apiClient.get('/admin/artisans/', { params });
      return response.data.data;
    } catch (error) {
      console.error('Error fetching artisans:', error);
      throw error;
    }
  }

  /**
   * Mark a job as completed (admin override)
   * @param jobId - UUID of the job
   * @param notes - Optional notes for admin action
   */
  async markJobComplete(jobId: string, notes?: string): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/jobs/${jobId}/`, {
        action: 'complete',
        notes,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error completing job:', error);
      throw error;
    }
  }

  /**
   * Process a refund for a job (full escrow refund to client)
   * @param jobId - UUID of the job
   * @param amount - Ignored by backend (always full escrow); kept for API compat
   * @param reason - Reason for refund
   */
  async processRefund(
    jobId: string,
    amount?: number,
    reason?: string
  ): Promise<any> {
    try {
      const response = await apiClient.post(`/admin/jobs/${jobId}/refund/`, {
        reason: reason || 'Admin initiated refund',
      });
      return response.data.data;
    } catch (error) {
      console.error('Error processing refund:', error);
      throw error;
    }
  }

  /**
   * Dispute a job (admin escalation)
   * @param jobId - UUID of the job
   * @param reason - Reason for dispute
   */
  async disputeJob(jobId: string, reason: string): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/jobs/${jobId}/`, {
        action: 'dispute',
        reason,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error disputing job:', error);
      throw error;
    }
  }

  /**
   * Cancel a job (admin action)
   */
  async cancelJob(jobId: string, reason: string): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/jobs/${jobId}/`, {
        action: 'cancel',
        reason,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error cancelling job:', error);
      throw error;
    }
  }

  /**
   * Edit a job (title, status, budget_max, location)
   */
  async updateJob(jobId: string, data: {
    title?: string;
    status?: string;
    budget_max?: number;
    location?: string;
  }): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/jobs/${jobId}/`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating job:', error);
      throw error;
    }
  }

  /**
   * Permanently delete a job
   */
  async deleteJob(jobId: string): Promise<any> {
    try {
      const response = await apiClient.delete(`/admin/jobs/${jobId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  }
}

export default new JobService();
